import { Chat, DexVoice, Exchange, Flow, Verify, VerifyPracticePal } from 'models/enums'

const HIDDEN_PRODUCT_IDS = [
    VerifyPracticePal.id,
    Verify.id,
    Flow.id,
    DexVoice.id,
    Chat.id,
    Exchange.id,
]

const HIDDEN_PRODUCT_VALUES: string[] = [
    VerifyPracticePal.value,
    Verify.value,
    Flow.value,
    DexVoice.value,
    Chat.value,
    Exchange.value,
]

const SIMPLIFEYE_PRODUCT_VALUES = ['payments', 'direct_scheduling', 'amplify', 'chairfill']

const PRODUCT_DISPLAY_NAME_MAP: Record<string, string> = {
    Amplify: 'Live Webchat',
    'Direct Scheduling': 'Online Scheduling',
    Payments: 'Simplifeye Payments',
}

export interface DisplayProduct {
    id: string | number
    value: string
    displayName: string
    active: boolean
    isVyne?: boolean
}

function transformProductDisplayName(displayName: string): string {
    let result = displayName
    for (const [original, replacement] of Object.entries(PRODUCT_DISPLAY_NAME_MAP)) {
        result = result.replace(original, replacement)
    }
    return result
}

function isHiddenProductById(productId: number): boolean {
    return HIDDEN_PRODUCT_IDS.includes(productId)
}

function isHiddenProductByValue(value: string): boolean {
    return HIDDEN_PRODUCT_VALUES.includes(value)
}

function mapLocationProductToDisplayProduct(product: Models.PracticeLocationProduct): DisplayProduct {
    return {
        id: product.product.id,
        value: product.product.value,
        displayName: transformProductDisplayName(product.product.display_name),
        active: product.active,
        isVyne: false,
    }
}

function mapVyneProductToDisplayProduct(productName: string): DisplayProduct {
    const cleanName = productName.replaceAll('\n', '')
    return {
        id: cleanName,
        value: cleanName,
        displayName: cleanName,
        active: true,
        isVyne: true,
    }
}

export function getFilteredLocationProducts(
    products: Models.PracticeLocationProduct[] | undefined,
): DisplayProduct[] {
    if (!products) return []

    return products
        .filter(product => !isHiddenProductById(product.product_id))
        .map(mapLocationProductToDisplayProduct)
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
}

export function getFilteredVyneProducts(vyneProducts: string[] | undefined): DisplayProduct[] {
    if (!vyneProducts) return []

    return vyneProducts
        .filter(product => !isHiddenProductByValue(product))
        .map(mapVyneProductToDisplayProduct)
        .sort((a, b) => a.displayName.localeCompare(b.displayName))
}

export function getMergedProductsForVyneLocation(
    locationProducts: Models.PracticeLocationProduct[] | undefined,
    vyneProducts: string[] | undefined,
): DisplayProduct[] {
    const simplifeyeProducts = getFilteredLocationProducts(locationProducts)
        .filter(product => SIMPLIFEYE_PRODUCT_VALUES.includes(product.value) && product.active)

    const vyneDisplayProducts = getFilteredVyneProducts(vyneProducts)

    const allProducts = [...simplifeyeProducts, ...vyneDisplayProducts]

    const uniqueProducts = allProducts.reduce<DisplayProduct[]>((acc, product) => {
        const exists = acc.some(p => p.displayName === product.displayName)
        if (!exists) {
            acc.push(product)
        }
        return acc
    }, [])

    return uniqueProducts.sort((a, b) => a.displayName.localeCompare(b.displayName))
}

export function getDisplayProducts(location: Models.PracticeLocation): DisplayProduct[] {
    if (location.isVyneControlled) {
        return getMergedProductsForVyneLocation(location.products, location.vyneProducts)
    }
    return getFilteredLocationProducts(location.products)
}

export function isLocationInactive(location: Models.PracticeLocation): boolean {
    const products = getDisplayProducts(location)
    return products.length === 0 || products.every(product => !product.active)
}

export function isVyneProductsLoading(location: Models.PracticeLocation): boolean {
    return Boolean(location.isVyneControlled && location.facilityId && location.vyneProducts === undefined)
}
